import { List } from "immutable";
import _ from "lodash";
import * as React from "react";
import * as BS from "react-bootstrap";
import * as Comp from "../../../components";
import { ComponentWithValidatedForm } from "../../../components/validation";
import { Statuses } from "../../../domain";
import * as Forms from "../../../forms";
import * as M from "../../../models";
import { Api } from "../../../services/Api";
import { DateHelper, Utility } from "../../../utils";
import { PlacementEditor } from "./components";

interface OwnProps {
    api: Api;
    cancelLink: JSX.Element;
    emailTemplates: List<M.Email.EmailTemplate>;
    eventId: number;
    onSubmit: (model: Forms.AdminAbstract.AbstractTypeData, body: any) => void;
}

interface OwnState {
}

export class EditTypeForm extends ComponentWithValidatedForm<OwnProps, OwnState> {

    public constructor(props: any) {
        super(props);
        const formModel = new Forms.AdminAbstract.AbstractType();
        this.state = {
            ...this.getInitialState(formModel),
        };
    }

    public setFormData(data: M.Abstract.AbstractTypeEdit) {
        const formModel = this.getFormModelData<Forms.AdminAbstract.AbstractTypeData>();
        formModel.active = Statuses.isActive(data.statusId),
        formModel.confirmationEmailTemplate = data.confirmationEmailTemplate
            ? M.Tag.tagToReactSelectOption(data.confirmationEmailTemplate)
            : null,
        formModel.description = data.description;
        formModel.graderCommentValidationText = data.graderCommentValidationText;
        formModel.gradingEnabled = data.gradingEnabled;
        formModel.id = data.id;
        formModel.invitationDeadline = Utility.fromApiDate(data.invitationDeadline) || null;
        formModel.invitationEmailTemplate = data.invitationEmailTemplate
            ? M.Tag.tagToReactSelectOption(data.invitationEmailTemplate)
            : null,
        formModel.isHiddenOnForm = data.isHiddenOnForm;
        formModel.name = data.name;
        formModel.placements = data.placements.map((p, i) => ({
            confirmationEmailText: p.confirmationEmailText,
            displayName: p.displayName || null,
            id: p.id,
            index: i,
            invitationEmailText: p.invitationEmailText,
            isDeleted: false,
            isNew: false,
            placementId: p.placementId,
            sessionDuration: p.sessionDuration,
        })),
        formModel.sessionDuration = data.sessionDuration;
        formModel.shortName = data.shortName;
        formModel.submitDeadline = Utility.fromApiDate(data.submitDeadline) || null;
        formModel.uploadDeadline = data.uploadDeadline
            ? (Utility.fromApiDate(data.uploadDeadline) || null)
            : null;

        this.updateFormModelData(formModel);
    }

    public render() {
        const formModel = this.getFormModelData<Forms.AdminAbstract.AbstractTypeData>();
        const templates: M.Generic.ReactSelectOption[] = this.props.emailTemplates
            .map((t: M.Email.EmailTemplate) => ({
                label: t.name,
                value: t.id,
            }))
            .toArray();

        const graderCommentValHint = `${(formModel.graderCommentValidationText || "").length} out of 200 characters`;

        return <Comp.Validation.ValidatedForm
            formComponent={this}
            model={this.state.formModel.data}
            onValidSubmit={async (model) => this.handleSubmit(model)}
            schema={this.state.formModel.rules}
            ref={(ref: any) => this.form = ref}
        >

            <Comp.Editors.CheckboxField formComponent={this} name="active" />
            <Comp.Editors.CheckboxField formComponent={this} name="isHiddenOnForm" />
            <Comp.Editors.TextField formComponent={this} name="name" />
            <Comp.Editors.TextField formComponent={this} name="shortName" />
            <Comp.Editors.FormField name="">Times are optional. If left blank, time indicates beginning of the day.</Comp.Editors.FormField>
            <Comp.Editors.DateAndTimePickerField formComponent={this} name="submitDeadline" />
            <Comp.Editors.DateAndTimePickerField formComponent={this} name="uploadDeadline" />
            <Comp.Editors.DateAndTimePickerField formComponent={this} name="invitationDeadline" />

            <Comp.Editors.SelectField
                formComponent={this}
                name="invitationEmailTemplate"
                select={{
                    className: "txt-med",
                    clearable: true,
                    options: templates,
                    placeholder: "Select email template...",
                }}
            />

            <Comp.Editors.SelectField
                formComponent={this}
                name="confirmationEmailTemplate"
                select={{
                    className: "txt-med",
                    clearable: true,
                    options: templates,
                    placeholder: "Select email template...",
                }}
            />

            <Comp.Editors.RichTextField formComponent={this} isAdmin name="description" />
            <Comp.Editors.NumberField formComponent={this} name="sessionDuration" labelHint="In minutes" />

            <hr />

            <h2>Grading</h2>

            <Comp.Editors.CheckboxField formComponent={this} name="gradingEnabled" />

            <BS.Collapse in={formModel.gradingEnabled} timeout={125}>
                <div>
                    <Comp.Editors.TextAreaField
                        formComponent={this}
                        name="graderCommentValidationText"
                        labelHint="Validation text that appears to graders if they reject an abstract without specifying a reason."
                        hint={graderCommentValHint}
                    />
                </div>
            </BS.Collapse>

            <hr />

            <h2>Placements</h2>

            <PlacementEditor formComponent={this} />

            <hr />

            <Comp.Editors.SubmitField cancelLink={this.props.cancelLink} label="Save" showProcessing={this.state.formProcessing} />

        </Comp.Validation.ValidatedForm>;
    }

    private handleSubmit(model: Forms.AdminAbstract.AbstractTypeData) {
        const body = {
            active: model.active,
            confirmationEmailTemplateId: model.confirmationEmailTemplate
                ? model.confirmationEmailTemplate.value
                : null,
            description: model.description,
            graderCommentValidationText: model.graderCommentValidationText,
            gradingEnabled: model.gradingEnabled,
            eventId: this.props.eventId,
            id: model.id,
            invitationDeadline: DateHelper.formatDateWithoutZone(model.invitationDeadline || new Date()),
            invitationEmailTemplateId: model.invitationEmailTemplate
                ? model.invitationEmailTemplate.value
                : null,
            isHiddenOnForm: model.isHiddenOnForm,
            name: model.name,
            placements: model.placements
                .filter((x) => !(x.isNew && x.isDeleted))
                .map((x) => ({
                    confirmationEmailText: x.confirmationEmailText,
                    displayName: x.displayName || null,
                    id: x.id,
                    invitationEmailText: x.invitationEmailText,
                    isDeleted: x.isDeleted,
                    placementId: x.placementId,
                    sessionDuration: x.sessionDuration,
                })),
            sessionDuration: model.sessionDuration,
            shortName: model.shortName,
            submitDeadline: DateHelper.formatDateWithoutZone(model.submitDeadline || new Date()),
            uploadDeadline: model.uploadDeadline
                ? DateHelper.formatDateWithoutZone(model.uploadDeadline)
                : null,
        };

        this.props.onSubmit(model, body);
    }
}



// WEBPACK FOOTER //
// ./src/containers/adminAbstracts/editType/EditTypeForm.tsx